"use client";

import { useState, useEffect } from "react";
import {
  Mail,
  Send,
  Phone,
  Calculator,
  Clock,
  TrendingUp,
  CheckCircle,
  Calendar,
  Copy,
  Check,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

// Declare Calendly types for TypeScript
declare global {
  interface Window {
    Calendly: {
      initPopupWidget: (options: { url: string }) => void;
      initBadgeWidget: (options: {
        url: string;
        text: string;
        color: string;
        textColor: string;
      }) => void;
    };
  }
}

const ContactForm = () => {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    company: "",
    currentProcess: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedItems, setCopiedItems] = useState<{ [key: string]: boolean }>(
    {}
  );

  const { toast } = useToast();

  // Load Calendly widget script
  useEffect(() => {
    // Load Calendly styles
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Load Calendly script
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      const existingLink = document.querySelector(
        'link[href="https://assets.calendly.com/assets/external/widget.css"]'
      );
      const existingScript = document.querySelector(
        'script[src="https://assets.calendly.com/assets/external/widget.js"]'
      );

      if (existingLink) {
        document.head.removeChild(existingLink);
      }
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const openCalendly = () => {
    // Check if Calendly is loaded
    if (typeof window !== "undefined" && window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/abduldev/15-minute-meeting",
      });
    } else {
      // Fallback: open in new tab if widget fails
      window.open("https://calendly.com/abduldev/15-minute-meeting", "_blank");
    }
  };

  const copyToClipboard = async (text: string, type: string, key: string) => {
    try {
      // Use modern clipboard API
      await navigator.clipboard.writeText(text);

      // Set copied state
      setCopiedItems((prev) => ({ ...prev, [key]: true }));

      // Show success toast
      toast({
        title: `${type} copied!`,
        description: `${text} has been copied to your clipboard.`,
      });

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedItems((prev) => ({ ...prev, [key]: false }));
      }, 2000);
    } catch {
      // Fallback: show the text for manual copying
      toast({
        title: "Copy not supported",
        description: `Please copy manually: ${text}`,
        variant: "destructive",
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      toast({
        title: "Free Automation Audit Requested!",
        description:
          "I'll analyze your workflow and send you a custom ROI report within 24 hours.",
      });

      setFormState({
        name: "",
        email: "",
        company: "",
        currentProcess: "",
        message: "",
      });
    }, 1500);
  };

  return (
    <section
      id="contact"
      className="py-20 px-6 bg-gradient-to-br from-background via-primary/5 to-background"
    >
      <div className="container max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Save $50K-$200K Annually?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Get your free automation audit and discover exactly how much time
              and money AI can save your business. No sales pitch—just honest
              analysis.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Value Props */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-8"
          >
            <div className="bg-card border border-border rounded-2xl p-6">
              <h3 className="text-2xl font-bold mb-6 flex items-center">
                <Calculator className="w-6 h-6 mr-3 text-primary" />
                What You&apos;ll Get (Free)
              </h3>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Custom ROI Analysis</h4>
                    <p className="text-sm text-muted-foreground">
                      Detailed breakdown of potential savings for your specific
                      workflows
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Automation Roadmap</h4>
                    <p className="text-sm text-muted-foreground">
                      Step-by-step plan to implement AI in your business
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold">Quick Wins Identified</h4>
                    <p className="text-sm text-muted-foreground">
                      Immediate opportunities to save time and reduce costs
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center bg-primary/5 border border-primary/20 rounded-xl p-4">
                <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                <div className="text-lg font-bold">24 Hours</div>
                <div className="text-xs text-muted-foreground">
                  Response Time
                </div>
              </div>
              <div className="text-center bg-primary/5 border border-primary/20 rounded-xl p-4">
                <TrendingUp className="w-6 h-6 text-primary mx-auto mb-2" />
                <div className="text-lg font-bold">6.5x ROI</div>
                <div className="text-xs text-muted-foreground">
                  Average Return
                </div>
              </div>
              <div className="text-center bg-primary/5 border border-primary/20 rounded-xl p-4">
                <CheckCircle className="w-6 h-6 text-primary mx-auto mb-2" />
                <div className="text-lg font-bold">100% Free</div>
                <div className="text-xs text-muted-foreground">
                  No Obligation
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Prefer to Talk?</h3>
              <div className="space-y-3">
                <div
                  className="flex items-center gap-3 cursor-pointer hover:bg-accent/5 rounded-lg p-2 -m-2 transition-colors duration-200 group"
                  onClick={openCalendly}
                >
                  <div className="p-2 rounded-full bg-accent/10 text-accent group-hover:bg-accent/20 transition-colors duration-200">
                    <Calendar size={16} />
                  </div>
                  <div>
                    <p className="text-sm font-medium group-hover:text-accent transition-colors duration-200">
                      Schedule a 15-min call
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <a
                    href="mailto:contact@abduldev.co"
                    className="flex items-center gap-3 cursor-pointer hover:bg-accent/5 rounded-lg p-2 -m-2 transition-colors duration-200 group flex-1"
                  >
                    <div className="p-2 rounded-full bg-accent/10 text-accent group-hover:bg-accent/20 transition-colors duration-200">
                      <Mail size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-medium group-hover:text-accent transition-colors duration-200">
                        contact@abduldev.co
                      </p>
                    </div>
                  </a>
                  <button
                    onClick={() =>
                      copyToClipboard("contact@abduldev.co", "Email", "email")
                    }
                    className="p-2 rounded-lg hover:bg-accent/10 transition-all duration-200 text-muted-foreground hover:text-accent"
                    title={copiedItems.email ? "Copied!" : "Copy email"}
                  >
                    <motion.div
                      key={copiedItems.email ? "check" : "copy"}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {copiedItems.email ? (
                        <Check size={14} className="text-green-600" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </motion.div>
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <a
                    href="tel:+447832721345"
                    className="flex items-center gap-3 cursor-pointer hover:bg-accent/5 rounded-lg p-2 -m-2 transition-colors duration-200 group flex-1"
                  >
                    <div className="p-2 rounded-full bg-accent/10 text-accent group-hover:bg-accent/20 transition-colors duration-200">
                      <Phone size={16} />
                    </div>
                    <div>
                      <p className="text-sm font-medium group-hover:text-accent transition-colors duration-200">
                        +44 7832 721 345
                      </p>
                    </div>
                  </a>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        "+44 7832 721 345",
                        "Phone number",
                        "phone"
                      )
                    }
                    className="p-2 rounded-lg hover:bg-accent/10 transition-all duration-200 text-muted-foreground hover:text-accent"
                    title={copiedItems.phone ? "Copied!" : "Copy phone number"}
                  >
                    <motion.div
                      key={copiedItems.phone ? "check" : "copy"}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {copiedItems.phone ? (
                        <Check size={14} className="text-green-600" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </motion.div>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
            className="bg-card border border-border rounded-2xl p-8"
          >
            <h3 className="text-xl font-bold mb-6">
              Get Your Free Automation Audit
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formState.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="company" className="text-sm font-medium">
                  Company
                </label>
                <Input
                  id="company"
                  name="company"
                  value={formState.company}
                  onChange={handleChange}
                  placeholder="Your company name"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="currentProcess" className="text-sm font-medium">
                  What manual process takes most of your time? *
                </label>
                <Input
                  id="currentProcess"
                  name="currentProcess"
                  value={formState.currentProcess}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Data entry, customer follow-ups, reporting..."
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium">
                  Additional Details
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  placeholder="Tell me more about your current challenges and goals..."
                  rows={4}
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full text-base py-6 rounded-full"
              >
                {isSubmitting ? (
                  <>Analyzing Your Workflow...</>
                ) : (
                  <>
                    Get My Free ROI Analysis
                    <Send size={18} className="ml-2" />
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                No spam, no sales calls. Just valuable insights delivered to
                your inbox within 24 hours.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
